/* Canned scenarios for the advisor simulator's bulk-mode dropdown.
 *
 * These are the same 20 we ran through the simulator's regression sweep
 * (see chat history 2026-05-07): five categories × diverse triggers,
 * shaped to exercise each of the 7 live advisor variants. Picking one
 * from the dropdown populates the transcript editor; the agent can then
 * tweak before hitting Run.
 *
 * Keep the list tight — these are demo-worthy retention call shapes,
 * not exhaustive coverage. New ones earn a slot only if they cover a
 * gap (a variant that doesn't fire elsewhere, a real edge case the
 * production transcripts surfaced).
 */

export type SimRole = "customer" | "agent";

export type SimScenario = {
  id: string;
  category: string;
  label: string;
  turns: { role: SimRole; text: string }[];
};

export const SIM_SCENARIOS: SimScenario[] = [
  // ── Bill shock ─────────────────────────────────────────────────────────
  {
    id: "bill_small_bump",
    category: "Bill shock",
    label: "Small bill bump (autopay miss)",
    turns: [
      { role: "customer", text: "Hey my bill went up fifteen bucks this month, what changed" },
      { role: "agent",    text: "Let me look at your account real quick" },
      { role: "customer", text: "Last month was eighty-five, now it's a hundred" },
      { role: "agent",    text: "I see — your autopay discount didn't apply this cycle" },
      { role: "customer", text: "How do I make sure that doesn't happen again" },
      { role: "agent",    text: "Let me see what we can do to make this right" },
    ],
  },
  {
    id: "bill_large_bump",
    category: "Bill shock",
    label: "Large bill jump (+$80)",
    turns: [
      { role: "customer", text: "My bill jumped eighty dollars and nobody told me" },
      { role: "agent",    text: "I'm sorry that happened, let me check what changed" },
      { role: "customer", text: "I went from one twenty to two hundred without warning" },
      { role: "agent",    text: "I see — three line promos all expired this cycle" },
      { role: "customer", text: "This is unacceptable, I'm furious" },
      { role: "agent",    text: "I hear you, let me look at what we can do" },
    ],
  },
  {
    id: "promo_expired_explicit",
    category: "Bill shock",
    label: "Intro rate ended",
    turns: [
      { role: "customer", text: "My intro rate ended and now it's way more expensive" },
      { role: "agent",    text: "Yes, the promo was for twelve months. Let me see your options" },
      { role: "customer", text: "What's available — I can't pay this much going forward" },
      { role: "agent",    text: "Let me look at what we can offer for your loyalty" },
    ],
  },
  {
    id: "autopay_failed",
    category: "Bill shock",
    label: "Autopay failure + late fees",
    turns: [
      { role: "customer", text: "Why am I getting late fees when I'm on autopay" },
      { role: "agent",    text: "Let me look at your payment history" },
      { role: "customer", text: "My card expired but you guys never told me" },
      { role: "agent",    text: "I see — autopay failed twice and triggered late fees" },
      { role: "customer", text: "Can you remove those fees, this isn't my fault" },
      { role: "agent",    text: "Let me see what we can do for you" },
    ],
  },

  // ── Competitor poach ──────────────────────────────────────────────────
  {
    id: "att_match",
    category: "Competitor poach",
    label: "AT&T direct match",
    turns: [
      { role: "customer", text: "I want to cancel — AT&T is offering me forty bucks a line" },
      { role: "agent",    text: "Sorry to hear that, what plan are they offering exactly" },
      { role: "customer", text: "Three lines, one twenty total, unlimited data" },
      { role: "agent",    text: "That's their intro rate — does it stay there" },
      { role: "customer", text: "They said three months then it goes up" },
      { role: "agent",    text: "Let me show you what we can do for your loyalty" },
    ],
  },
  {
    id: "tmobile_family",
    category: "Competitor poach",
    label: "T-Mobile family plan",
    turns: [
      { role: "customer", text: "T-Mobile has a family plan that's way cheaper" },
      { role: "agent",    text: "What does the T-Mobile plan look like for your family" },
      { role: "customer", text: "Four lines unlimited, one fifty total, includes Netflix" },
      { role: "agent",    text: "Got it. Their network coverage where you live — have you checked" },
      { role: "customer", text: "Honestly no, but the price is hard to ignore" },
      { role: "agent",    text: "Let me put together what we can offer to keep you" },
    ],
  },
  {
    id: "verizon_5g_pitch",
    category: "Competitor poach",
    label: "Verizon 5G bundle pitch",
    turns: [
      { role: "customer", text: "Verizon is pushing this 5G home internet plus phone bundle" },
      { role: "agent",    text: "What does the bundle include" },
      { role: "customer", text: "Phone, home internet, and unlimited hotspot for two hundred" },
      { role: "agent",    text: "And how reliable is their 5G home in your area" },
      { role: "customer", text: "Spotty actually, but the price tag is tempting" },
    ],
  },
  {
    id: "already_switched",
    category: "Competitor poach",
    label: "Already activated competitor SIM",
    turns: [
      { role: "customer", text: "I'm calling to cancel — I already got a SIM from T-Mobile" },
      { role: "agent",    text: "Got it. What ultimately drove the switch" },
      { role: "customer", text: "Just price, nothing personal" },
      { role: "agent",    text: "Understood. When do you want service shut off" },
      { role: "customer", text: "End of this billing cycle is fine" },
    ],
  },

  // ── Service issue ─────────────────────────────────────────────────────
  {
    id: "internet_drops_diag",
    category: "Service issue",
    label: "Internet drops at peak time",
    turns: [
      { role: "customer", text: "My internet drops three or four times a day for two weeks" },
      { role: "agent",    text: "That's frustrating. When does it usually drop" },
      { role: "customer", text: "Mostly evenings around seven or eight" },
      { role: "agent",    text: "Peak time. Have you tried restarting the modem" },
      { role: "customer", text: "Yeah a bunch of times, same thing happens" },
      { role: "agent",    text: "Let me run a diagnostic and check for outages in your area" },
    ],
  },
  {
    id: "no_service_3_days",
    category: "Service issue",
    label: "No service for 3 days (severe)",
    turns: [
      { role: "customer", text: "I've had no service for three straight days" },
      { role: "agent",    text: "That's a serious issue, let me check the network status" },
      { role: "customer", text: "I work from home, this is killing my business" },
      { role: "agent",    text: "I see an outage in your zip — I'll open a Tier-2 ticket now" },
      { role: "customer", text: "How long until it's fixed" },
      { role: "agent",    text: "ETA is twenty-four hours, I'll text you status" },
    ],
  },
  {
    id: "slow_speeds",
    category: "Service issue",
    label: "Slow data speeds",
    turns: [
      { role: "customer", text: "My data speeds are super slow lately, like 1mbps" },
      { role: "agent",    text: "When did this start" },
      { role: "customer", text: "About a week ago, both at home and out" },
      { role: "agent",    text: "Let me check if you've hit a throttle threshold" },
      { role: "customer", text: "I've definitely been streaming a lot of video" },
    ],
  },
  {
    id: "phone_wont_activate",
    category: "Service issue",
    label: "New phone won't activate",
    turns: [
      { role: "customer", text: "I just got a new phone and it won't activate" },
      { role: "agent",    text: "Let me check the activation status on the line" },
      { role: "customer", text: "It's been two days, I have no service at all" },
      { role: "agent",    text: "I see — SIM didn't provision properly, I'll reissue now" },
      { role: "customer", text: "How fast can you get me a working SIM" },
    ],
  },

  // ── Cancellation ──────────────────────────────────────────────────────
  {
    id: "moving_abroad",
    category: "Cancellation",
    label: "Moving abroad (graceful close)",
    turns: [
      { role: "customer", text: "I want to cancel, I'm moving to Spain next month" },
      { role: "agent",    text: "Congratulations on the move. When's your last day needing service" },
      { role: "customer", text: "End of the month please" },
      { role: "agent",    text: "Got it. Are you keeping your number for now" },
      { role: "customer", text: "No, I'll get a Spanish number when I get there" },
    ],
  },
  {
    id: "business_closure",
    category: "Cancellation",
    label: "Business closure (4 lines)",
    turns: [
      { role: "customer", text: "I'm closing my business and need to cancel four lines" },
      { role: "agent",    text: "I'm sorry to hear that. When do you need service to end" },
      { role: "customer", text: "Next Friday is the last day" },
      { role: "agent",    text: "Understood. Do you want a final invoice mailed or emailed" },
      { role: "customer", text: "Email please, same address on file" },
    ],
  },
  {
    id: "just_cancel_no_reason",
    category: "Cancellation",
    label: "Just cancel, no reason given",
    turns: [
      { role: "customer", text: "I just want to cancel today" },
      { role: "agent",    text: "I can help. Can I ask what's prompting the change" },
      { role: "customer", text: "I'd rather not say, just process it" },
      { role: "agent",    text: "Understood. Effective today or end of cycle" },
      { role: "customer", text: "Today, the sooner the better" },
    ],
  },
  {
    id: "rate_hike_anger",
    category: "Cancellation",
    label: "Rate hike — \"third time this year\"",
    turns: [
      { role: "customer", text: "You raised my rate again without telling me, this is the third time" },
      { role: "agent",    text: "I see the rate change. Let me check what's available" },
      { role: "customer", text: "I'm done — every year you sneak another increase" },
      { role: "agent",    text: "I understand, that's frustrating. Let me see what we can do" },
      { role: "customer", text: "If you can't lock my price I'm gone" },
    ],
  },

  // ── Upsell / addition ─────────────────────────────────────────────────
  {
    id: "add_kid_line",
    category: "Upsell",
    label: "Add line for child",
    turns: [
      { role: "customer", text: "My daughter is getting her first phone, can I add a line" },
      { role: "agent",    text: "Of course. How old is she" },
      { role: "customer", text: "Twelve, so basic stuff and parental controls" },
      { role: "agent",    text: "We have a kids-safe add-on. Let me show you the options" },
    ],
  },
  {
    id: "upgrade_to_unlimited",
    category: "Upsell",
    label: "Upgrade — keep hitting data cap",
    turns: [
      { role: "customer", text: "I keep hitting my data cap, can I upgrade my plan" },
      { role: "agent",    text: "How much data are you using" },
      { role: "customer", text: "Like fifteen gigs a month, I have a ten gig plan" },
      { role: "agent",    text: "Let me show you the unlimited tier" },
    ],
  },

  // ── Risk / sentiment ──────────────────────────────────────────────────
  {
    id: "lawyer_threat",
    category: "Risk",
    label: "Lawyer threat (compliance flag)",
    turns: [
      { role: "customer", text: "I'm done — you charged me for a phone I never received" },
      { role: "agent",    text: "I'm sorry, let me look into that immediately" },
      { role: "customer", text: "If this isn't fixed today I'm calling my lawyer" },
      { role: "agent",    text: "I understand, let me escalate this right now" },
    ],
  },
  {
    id: "frustration_escalates",
    category: "Risk",
    label: "Repeat-call frustration",
    turns: [
      { role: "customer", text: "My bill is wrong again" },
      { role: "agent",    text: "Let me pull it up" },
      { role: "customer", text: "This is the FIFTH time I've called about this in two months" },
      { role: "agent",    text: "I hear you, that's clearly broken — let me get this fixed" },
      { role: "customer", text: "I'm at the end of my rope here" },
    ],
  },
];

/** Render a scenario's turns as the textarea-friendly format the
 * simulator's parser already understands ("CUSTOMER: ...\nAGENT: ..."). */
export function scenarioToTranscriptText(s: SimScenario): string {
  return s.turns
    .map((t) => `${t.role === "customer" ? "CUSTOMER" : "AGENT"}: ${t.text}`)
    .join("\n");
}
